import type React from 'react'
import {useCallback, useEffect, useInsertionEffect, useRef, useState} from 'react'

// Delay before opening the popover on pointer hover. Long enough that quickly
// sweeping the cursor across an anchor on the way somewhere else doesn't open
// (and immediately close) the popover, short enough to feel responsive when
// the user actually pauses to read. Click and keyboard focus bypass this and
// open immediately.
const HOVER_OPEN_DELAY_MS = 200

// Delay between pointer/focus leaving the anchor area and the popover closing.
// Long enough for users to move from the anchor to the popover (or vice-versa)
// without losing it, short enough that an accidental hover dismisses quickly.
const HOVER_CLOSE_DELAY_MS = 200

// Module-scoped registry of the currently-open popover's "close immediately"
// callback. When a popover opens we close any other open popover first so the
// user never sees two popovers at once when hovering across anchors (no
// flicker from the trailing close-delay grace period of the previous one).
let closeCurrentlyOpen: (() => void) | null = null
let closeCurrentlyOpenIdentity: object | null = null

export type HoverPopoverHandlers<
  TAnchor extends HTMLElement = HTMLDivElement,
  TContent extends HTMLElement = HTMLDivElement,
> = {
  /** True while the popover should be rendered as open. */
  open: boolean
  /** Spread onto the element that wraps the anchor and reacts to pointer enter/leave. */
  anchorRegionProps: {
    ref: React.RefObject<TAnchor | null>
    onPointerEnter: () => void
    onPointerLeave: (event: React.PointerEvent<HTMLElement>) => void
  }
  /** Spread onto the focusable anchor element (typically a button). */
  anchorProps: {
    onClick: () => void
    onFocus: () => void
    onBlur: (event: React.FocusEvent<HTMLElement>) => void
  }
  /** Spread onto the popover content element so hovering it keeps it open. */
  contentProps: {
    ref: React.RefObject<TContent | null>
    onPointerEnter: () => void
    onPointerLeave: (event: React.PointerEvent<HTMLElement>) => void
  }
  /** Close the popover immediately, cancelling any pending open/close timers. */
  closeNow: () => void
}

type UseHoverPopoverOptions = {
  /**
   * Optional callback fired the first time the popover opens in a given
   * "open session". Reset whenever the popover closes, so reopening fires it
   * again. Useful for analytics.
   */
  onFirstOpen?: () => void
}

/**
 * Manages open/close state for a hover- and focus-triggered popover anchored
 * to a focusable element.
 *
 * Behavior:
 * - Hovering the anchor region opens the popover after a small delay so that
 *   quickly sweeping the cursor across the anchor doesn't flash it open.
 * - Click and keyboard focus open immediately.
 * - The popover stays open while the cursor (or keyboard focus) is in either
 *   the anchor region or the popover content, with a small grace period so the
 *   user can move between the two without it closing.
 * - Opening one popover instance immediately closes any other instance using
 *   this hook, so two popovers are never visible at once.
 *
 * Consumers are responsible for rendering an `AnchoredOverlay` (or equivalent)
 * conditionally on `open`, and spreading the returned props onto the anchor
 * region, the anchor element, and the popover content.
 */
export function useHoverPopover<
  TAnchor extends HTMLElement = HTMLDivElement,
  TContent extends HTMLElement = HTMLDivElement,
>({onFirstOpen}: UseHoverPopoverOptions = {}): HoverPopoverHandlers<TAnchor, TContent> {
  const [open, setOpen] = useState(false)
  const anchorRegionRef = useRef<TAnchor | null>(null)
  const contentRef = useRef<TContent | null>(null)
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasFiredFirstOpenRef = useRef(false)
  // True if the current open was triggered by keyboard focus (Tab) rather
  // than pointer (hover/click). Used to decide whether to blur the anchor
  // after AnchoredOverlay restores focus to it on close — we want to keep
  // focus for keyboard users, but not leave a focus ring on a hovered chip.
  const openedByKeyboardFocusRef = useRef(false)
  // AnchoredOverlay refocuses the anchor when it unmounts. Set this flag
  // before closing so the resulting synthetic focus event doesn't reopen us
  // and bounce off the cross-instance registry into a re-render loop.
  const ignoreNextFocusRef = useRef(false)
  // Stable identity for the module-scoped "currently open" registry, so each
  // hook instance can recognise its own entry.
  const identityRef = useRef<object>({})

  const onFirstOpenRef = useRef(onFirstOpen)
  useInsertionEffect(() => {
    onFirstOpenRef.current = onFirstOpen
  }, [onFirstOpen])

  const cancelScheduledOpen = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current)
      openTimeoutRef.current = null
    }
  }, [])

  const cancelScheduledClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }, [])

  const closeNow = useCallback(() => {
    cancelScheduledOpen()
    cancelScheduledClose()
    // Swallow AnchoredOverlay's focus-restoration on the anchor (see
    // ignoreNextFocusRef above).
    ignoreNextFocusRef.current = true
    setOpen(false)
    hasFiredFirstOpenRef.current = false
    if (closeCurrentlyOpenIdentity === identityRef.current) {
      closeCurrentlyOpenIdentity = null
      closeCurrentlyOpen = null
    }
  }, [cancelScheduledOpen, cancelScheduledClose])

  const openNow = useCallback(() => {
    cancelScheduledOpen()
    cancelScheduledClose()
    // Close any other instance's popover immediately so the user never sees
    // two overlapping popovers when sweeping the pointer across anchors.
    if (closeCurrentlyOpen && closeCurrentlyOpenIdentity !== identityRef.current) {
      closeCurrentlyOpen()
    }
    closeCurrentlyOpen = closeNow
    closeCurrentlyOpenIdentity = identityRef.current
    setOpen(true)
    if (!hasFiredFirstOpenRef.current) {
      hasFiredFirstOpenRef.current = true
      onFirstOpenRef.current?.()
    }
  }, [cancelScheduledOpen, cancelScheduledClose, closeNow])

  const scheduleOpen = useCallback(() => {
    cancelScheduledClose()
    // If already scheduled, don't reschedule (avoids resetting the delay when
    // the cursor moves around inside the anchor region).
    if (openTimeoutRef.current) return
    openTimeoutRef.current = setTimeout(() => {
      openTimeoutRef.current = null
      openedByKeyboardFocusRef.current = false
      openNow()
    }, HOVER_OPEN_DELAY_MS)
  }, [cancelScheduledClose, openNow])

  const handleAnchorClick = useCallback(() => {
    openedByKeyboardFocusRef.current = false
    openNow()
  }, [openNow])

  const handleAnchorFocus = useCallback(() => {
    if (ignoreNextFocusRef.current) {
      ignoreNextFocusRef.current = false
      return
    }
    // Mouse clicks fire `focus` before `click`, so default to keyboard here
    // and let `handleAnchorClick` flip it back if a click follows.
    openedByKeyboardFocusRef.current = true
    openNow()
  }, [openNow])

  const scheduleClose = useCallback(() => {
    cancelScheduledOpen()
    cancelScheduledClose()
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null
      closeNow()
    }, HOVER_CLOSE_DELAY_MS)
  }, [cancelScheduledOpen, cancelScheduledClose, closeNow])

  // pointerleave fires when the cursor moves between the anchor region and
  // the (portal-rendered) popover content because they're in different DOM
  // subtrees. If the cursor is moving into the other half of the hover region,
  // skip the scheduled close so the popover doesn't flash closed.
  const handleRegionPointerLeave = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const next = event.relatedTarget instanceof Node ? event.relatedTarget : null
      if (next) {
        if (anchorRegionRef.current?.contains(next)) return
        if (contentRef.current?.contains(next)) return
      }
      cancelScheduledOpen()
      scheduleClose()
    },
    [cancelScheduledOpen, scheduleClose],
  )

  const handleAnchorBlur = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      const next = event.relatedTarget instanceof Node ? event.relatedTarget : null
      if (next && contentRef.current?.contains(next)) return
      scheduleClose()
    },
    [scheduleClose],
  )

  useEffect(() => {
    const identity = identityRef.current
    return () => {
      cancelScheduledOpen()
      cancelScheduledClose()
      if (closeCurrentlyOpenIdentity === identity) {
        closeCurrentlyOpenIdentity = null
        closeCurrentlyOpen = null
      }
    }
  }, [cancelScheduledOpen, cancelScheduledClose])

  // Strip the focus ring AnchoredOverlay leaves behind on close for
  // pointer-driven opens. Keyboard-driven opens keep focus so Tab/Escape
  // navigation still works.
  useEffect(() => {
    if (open) return
    if (openedByKeyboardFocusRef.current) return
    const id = setTimeout(() => {
      const active = document.activeElement
      if (active instanceof HTMLElement && anchorRegionRef.current?.contains(active)) {
        // eslint-disable-next-line github/no-blur -- focus was never user-initiated; there's no previous element to restore to.
        active.blur()
      }
    }, 0)
    return () => clearTimeout(id)
  }, [open])

  return {
    open,
    anchorRegionProps: {
      ref: anchorRegionRef,
      onPointerEnter: scheduleOpen,
      onPointerLeave: handleRegionPointerLeave,
    },
    anchorProps: {
      onClick: handleAnchorClick,
      onFocus: handleAnchorFocus,
      onBlur: handleAnchorBlur,
    },
    contentProps: {
      ref: contentRef,
      onPointerEnter: cancelScheduledClose,
      onPointerLeave: handleRegionPointerLeave,
    },
    closeNow,
  }
}
